import type { Palette, ThemeTokens } from './tokens';

/**
 * Hazır renk paletleri ve tema şablonları. Adları `packages/i18n` içinde `theme.palettes.<anahtar>` ve
 * `theme.templates.<anahtar>` olarak çevrilir. Her palet testle WCAG AA kontrastından geçer.
 */
export interface PalettePreset {
  key: string;
  light: Palette;
  dark: Palette;
}

export const PALETTES: ReadonlyArray<PalettePreset> = [
  {
    // Zootopia: çam yeşili + kehribar (vitrinin bugünkü görünümü).
    key: 'pine',
    light: {
      background: '#f2f5f2',
      surface: '#ffffff',
      text: '#16211d',
      mutedText: '#4b5a54',
      border: '#d9e1dc',
      primary: '#0d5a4e',
      secondary: '#5b6770',
      accent: '#955c09',
      button: '#0d5a4e',
      buttonText: '#ffffff',
      buttonHover: '#0a4a40',
    },
    dark: {
      background: '#0e1412',
      surface: '#141c19',
      text: '#e3ebe7',
      mutedText: '#a9b8b1',
      border: '#25312c',
      primary: '#4fb5a3',
      secondary: '#a3b0b8',
      accent: '#e0a446',
      button: '#4fb5a3',
      buttonText: '#07130f',
      buttonHover: '#6cc6b6',
    },
  },
  {
    key: 'ocean',
    light: {
      background: '#f3f6f9',
      surface: '#ffffff',
      text: '#14202b',
      mutedText: '#4a5866',
      border: '#d6dee6',
      primary: '#0b5c8a',
      secondary: '#5a6b7b',
      accent: '#b4441c',
      button: '#0b5c8a',
      buttonText: '#ffffff',
      buttonHover: '#084a70',
    },
    dark: {
      background: '#0d141b',
      surface: '#131c25',
      text: '#e2eaf1',
      mutedText: '#a3b1bf',
      border: '#243241',
      primary: '#5fb0e0',
      secondary: '#9fb0c0',
      accent: '#f08a5d',
      button: '#5fb0e0',
      buttonText: '#06121b',
      buttonHover: '#82c3ea',
    },
  },
  {
    key: 'forest',
    light: {
      background: '#f4f5ef',
      surface: '#ffffff',
      text: '#1c2116',
      mutedText: '#51584a',
      border: '#dcdfd2',
      primary: '#3f5f1f',
      secondary: '#636957',
      accent: '#9c2f2f',
      button: '#3f5f1f',
      buttonText: '#ffffff',
      buttonHover: '#324c18',
    },
    dark: {
      background: '#11140d',
      surface: '#181c13',
      text: '#e6eadc',
      mutedText: '#b0b7a3',
      border: '#2c3324',
      primary: '#9cc46a',
      secondary: '#b3b9a5',
      accent: '#ec8c8c',
      button: '#9cc46a',
      buttonText: '#11140d',
      buttonHover: '#b2d48a',
    },
  },
  {
    key: 'amber',
    light: {
      background: '#f8f5ef',
      surface: '#ffffff',
      text: '#2a1f12',
      mutedText: '#5e5244',
      border: '#e6dccb',
      primary: '#8a4b08',
      secondary: '#6b5d4d',
      accent: '#0d5a4e',
      button: '#8a4b08',
      buttonText: '#ffffff',
      buttonHover: '#6f3c06',
    },
    dark: {
      background: '#17120b',
      surface: '#1f1810',
      text: '#f1e8da',
      mutedText: '#c2b5a2',
      border: '#3a2f22',
      primary: '#e0a446',
      secondary: '#bfae96',
      accent: '#4fb5a3',
      button: '#e0a446',
      buttonText: '#1a1205',
      buttonHover: '#ebb867',
    },
  },
  {
    key: 'plum',
    light: {
      background: '#f7f5fb',
      surface: '#ffffff',
      text: '#1f1a2b',
      mutedText: '#554d66',
      border: '#e0dbea',
      primary: '#5b3f9e',
      secondary: '#625a78',
      accent: '#a8451f',
      button: '#5b3f9e',
      buttonText: '#ffffff',
      buttonHover: '#4a3282',
    },
    dark: {
      background: '#131019',
      surface: '#1a1622',
      text: '#ece8f4',
      mutedText: '#b6aec8',
      border: '#2f2940',
      primary: '#b39ae8',
      secondary: '#b1a9c4',
      accent: '#f09a72',
      button: '#b39ae8',
      buttonText: '#131019',
      buttonHover: '#c7b4ef',
    },
  },
  {
    key: 'ink',
    light: {
      background: '#ffffff',
      surface: '#f6f6f6',
      text: '#111111',
      mutedText: '#505050',
      border: '#dddddd',
      primary: '#111111',
      secondary: '#505050',
      accent: '#b3262f',
      button: '#111111',
      buttonText: '#ffffff',
      buttonHover: '#333333',
    },
    dark: {
      background: '#0f0f0f',
      surface: '#191919',
      text: '#f0f0f0',
      mutedText: '#b0b0b0',
      border: '#2e2e2e',
      primary: '#f0f0f0',
      secondary: '#b0b0b0',
      accent: '#f07a80',
      button: '#f0f0f0',
      buttonText: '#111111',
      buttonHover: '#d0d0d0',
    },
  },
];

export const PALETTE_KEYS_LIST = PALETTES.map((p) => p.key);

const palette = (key: string) => PALETTES.find((p) => p.key === key)!;

/** Klinik: vitrinin bugünkü görünümü (varsayılan). */
const clinic: ThemeTokens = {
  schema: 1,
  palette: palette('pine').light,
  darkPalette: palette('pine').dark,
  colorScheme: 'auto',
  typography: {
    bodyFont: 'plex_sans',
    headingFont: 'plex_condensed',
    baseSize: 17,
    headingScale: 'normal',
    headingWeight: 600,
  },
  shape: { radius: 10, buttonShape: 'pill' },
  shadow: 'none',
  card: { style: 'bordered', imageRatio: 'square' },
  header: { layout: 'start', style: 'surface', sticky: false },
  footer: { style: 'surface' },
  productGrid: { columnsDesktop: 4, columnsMobile: 2 },
  productPage: { galleryPosition: 'start' },
  blog: { layout: 'grid' },
  form: { inputStyle: 'outline' },
};

export interface TemplatePreset {
  key: string;
  tokens: ThemeTokens;
}

export const TEMPLATES: ReadonlyArray<TemplatePreset> = [
  { key: 'clinic', tokens: clinic },
  {
    key: 'natural',
    tokens: {
      ...clinic,
      palette: palette('forest').light,
      darkPalette: palette('forest').dark,
      typography: {
        bodyFont: 'nunito_sans',
        headingFont: 'lora',
        baseSize: 17,
        headingScale: 'normal',
        headingWeight: 600,
      },
      shape: { radius: 16, buttonShape: 'rounded' },
      shadow: 'soft',
      card: { style: 'raised', imageRatio: 'portrait' },
      header: { layout: 'center', style: 'surface', sticky: false },
      footer: { style: 'dark' },
    },
  },
  {
    key: 'modern',
    tokens: {
      ...clinic,
      palette: palette('ink').light,
      darkPalette: palette('ink').dark,
      typography: {
        bodyFont: 'inter',
        headingFont: 'inter',
        baseSize: 16,
        headingScale: 'large',
        headingWeight: 700,
      },
      shape: { radius: 4, buttonShape: 'square' },
      shadow: 'none',
      card: { style: 'flat', imageRatio: 'portrait' },
      header: { layout: 'start', style: 'surface', sticky: true },
      footer: { style: 'dark' },
      productGrid: { columnsDesktop: 4, columnsMobile: 2 },
    },
  },
  {
    key: 'classic',
    tokens: {
      ...clinic,
      palette: palette('amber').light,
      darkPalette: null,
      colorScheme: 'light',
      typography: {
        bodyFont: 'source_sans',
        headingFont: 'merriweather',
        baseSize: 18,
        headingScale: 'compact',
        headingWeight: 700,
      },
      shape: { radius: 6, buttonShape: 'rounded' },
      shadow: 'soft',
      card: { style: 'bordered', imageRatio: 'square' },
      header: { layout: 'center', style: 'brand', sticky: false },
      footer: { style: 'brand' },
      productGrid: { columnsDesktop: 3, columnsMobile: 1 },
      blog: { layout: 'list' },
      form: { inputStyle: 'filled' },
    },
  },
];

export function defaultTokens(): ThemeTokens {
  return structuredClone(clinic);
}

export function templateTokens(key: string): ThemeTokens | null {
  const t = TEMPLATES.find((x) => x.key === key);
  return t ? structuredClone(t.tokens) : null;
}

/** Paleti mevcut token'lara uygular (yazı tipi ve düzen korunur). */
export function withPalette(tokens: ThemeTokens, key: string): ThemeTokens | null {
  const p = PALETTES.find((x) => x.key === key);
  if (!p) return null;
  return {
    ...tokens,
    palette: { ...p.light },
    darkPalette: tokens.darkPalette || tokens.colorScheme === 'auto' ? { ...p.dark } : null,
  };
}
